import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";

/**
 * Change your own password. Users whose password was set by someone else (new account, admin reset,
 * recovery) are sent here and cannot use the CRM until they have chosen their own.
 */
export function ChangePasswordPage() {
  const { user, refreshUser, logout } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const required = !!user?.must_change_password;
  const isSuperAdmin = String(user?.UserRole?.RoleCode || "").toUpperCase() === "SUPER_ADMIN";
  const minLength = isSuperAdmin ? 12 : 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < minLength) {
      toastError("Password too short", `Your new password must be at least ${minLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      toastError("Passwords do not match", "Type the same new password in both fields.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/api/User/ChangePassword", { old_password: current, password });
      await refreshUser();
      toastSuccess("Password changed", "You were signed out on your other devices.");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toastError("Could not change password", err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh w-full flex items-center justify-center p-4 py-8 bg-slate-100">
      <Card className="w-full max-w-md shadow-xl bg-white border-slate-200">
        <CardHeader className="space-y-1">
          {required ? (
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors self-start"
            >
              <ArrowLeft className="size-3.5" />
              Log out
            </button>
          ) : (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors"
            >
              <ArrowLeft className="size-3.5" />
              Back to dashboard
            </Link>
          )}
          <CardTitle className="text-xl font-bold">
            {required ? "Choose your own password" : "Change password"}
          </CardTitle>
          <CardDescription>
            {required
              ? "Your current password was set for you. Choose a new one that only you know before continuing."
              : "Changing your password signs you out on every other device."}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current" className="text-xs font-semibold">
                {required ? "Temporary password" : "Current password"}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input
                  id="current"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="pl-9 h-10 text-sm"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold">
                New password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-10 text-sm"
                  maxLength={72}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-semibold">
                Confirm new password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-9 h-10 text-sm"
                  maxLength={72}
                  required
                />
              </div>
            </div>

            <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              <ShieldCheck className="size-4 shrink-0 text-primary" />
              <span>
                At least {minLength} characters. A long phrase is easiest to remember. Common or previously leaked
                passwords, and your name or email, are not accepted.
              </span>
            </div>

            <Button type="submit" className="w-full h-10" disabled={submitting}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : "Save new password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
